<template>
  <div class="tg-login">
    <button
      class="tg-login__btn"
      type="button"
      :disabled="isLoading"
      @click="handleClick"
    >
      <img :src="TelegramIcon" class="tg-login__icon" alt="Telegram" />
      <span>{{ label }}</span>
    </button>

    <TelegramLoginWaitingModal
      v-model="showModal"
      :status="modalStatus"
      :ttl="challengeTtl"
      :challenge-id="challengeId"
      :error-reason="modalErrorReason"
      :bot-username="telegramBotUsername"
      @cancel="handleCancel"
      @retry="handleRetry"
      @close="handleClose"
    />

    <SponsorPopup v-model="showSponsorPopup" @confirmed="onSponsorConfirmed" />
  </div>
</template>

<script setup lang="ts">
import { computed, onUnmounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import TelegramIcon from '@/assets/images/icons/telegram_icon.svg'
import TelegramLoginWaitingModal from './TelegramLoginWaitingModal.vue'
import SponsorPopup from './SponsorPopup.vue'
import { TelegramLoginPollingService } from '../services/TelegramLoginPollingService'
import { botChatLinks, miniAppLinks, openTelegram } from '../utils/telegramDeepLink'
import { httpClient } from '@/shared/api/httpClient'
import { signInDone } from '../utils/signInDone'

/**
 * Вход через Telegram — один компонент на весь кабинет (решение оператора
 * 2026-09-23): главная страница (SocialAuthButtons), мост входа по ключу
 * (KeyLoginPage) и любое следующее место берут его как есть, а не
 * переписывают челлендж, опрос и окно ожидания заново.
 *
 * Поток: челлендж с бэкенда → телефон уходит в МИНИ-АПП (`startapp`),
 * десктоп — в переписку с ботом в новой вкладке → окно ожидания с QR →
 * опрос челленджа → токен → перезагрузка страницы (роутер сам уведёт в
 * кабинет). В режиме `register` без известного пригласителя сначала окно
 * «Укажите пригласителя» (SponsorPopup) — как у Google в SocialAuthButtons.
 */
const props = defineProps<{
  /** 'login' (по умолчанию) или 'register' */
  mode?: 'login' | 'register'
  /** Логин пригласителя для реферальной привязки */
  inviterLogin?: string
  /**
   * FLOW-V4 §6 п. 3 — вход ради ЭТОЙ страницы (перенос): телефон тоже уходит
   * в переписку с ботом, а не в мини-апп. Вкладка опрашивает челлендж и после
   * входа остаётся на том же адресе без перезагрузки (FLOW-V5 Б1) — сессия
   * продолжается здесь.
   */
  stayOnSite?: boolean
  /**
   * FLOW-V5 Б4 — адрес сессии переноса (`/tradein/s/{id}`). Есть — вход идёт
   * через `tgtradein_…`: бот закрепит перенос за аккаунтом и сразу попросит
   * номер, без общего «вы авторизовались».
   */
  tradeinResume?: string | null
}>()

const emit = defineEmits<{
  /**
   * OTP-R6c: Telegram подтвердил человека, но у аккаунта включено приложение-
   * аутентификатор — вход ещё не выдан. Pending-ключ уходит родителю, который
   * показывает шаг кода (LoginTotpStep); сохранять его как вход нельзя.
   */
  (e: 'totp-required', pendingToken: string): void
}>()

const { t } = useI18n()
// Роутера может не быть (компонент монтируют и голым) — тогда путь прежний,
// с перезагрузкой.
const router = useRouter() as ReturnType<typeof useRouter> | undefined

/**
 * Имя бота приезжает с бэкенда вместе с челленджем (`wp_sites.telegram_bot_name`).
 * Сборочная переменная осталась запасным вариантом: она однажды не попала в
 * сборку, и кнопка молча не работала — полагаться на неё одну больше нельзя.
 */
const telegramBotFallback = (import.meta.env.VITE_GONKAGAME_TELEGRAM_BOT_USERNAME || '').replace(/^@/, '')
const telegramBotUsername = ref(telegramBotFallback)

const isLoading = ref(false)
const showModal = ref(false)
const modalStatus = ref<'waiting' | 'success' | 'error'>('waiting')
const modalErrorReason = ref<'expired' | 'timeout' | 'error'>('error')
const challengeTtl = ref(300)
const challengeId = ref('')
const showSponsorPopup = ref(false)

let stopPolling: (() => void) | null = null

const storageKey = import.meta.env.VITE_GONKAGAME_TOKEN_KEY ?? 'authToken_igra'

const label = computed(() =>
  props.mode === 'register' ? t('auth.registerWithTelegram') : t('auth.loginWithTelegram'),
)

const getReferralCode = () => {
  const stored = props.inviterLogin || localStorage.getItem('partner_referral_code')
  if (stored) return stored
  const match = document.cookie.match(/(^| )referal=([^;]+)/)
  return match ? decodeURIComponent(match[2]) : ''
}

/**
 * Телефон определяем по ШИРИНЕ ЭКРАНА, а не по userAgent (решение оператора
 * 2026-09-21): список строк userAgent устаревает каждый год, а ширина окна —
 * это ровно то, по чему кабинет и так решает, как себя рисовать.
 */
const MOBILE_MAX_WIDTH = 768

const isMobile = (): boolean => window.innerWidth <= MOBILE_MAX_WIDTH

const needsSponsorPopup = () => props.mode === 'register' && !getReferralCode()

const handleClick = () => {
  if (needsSponsorPopup()) {
    showSponsorPopup.value = true
    return
  }
  startTelegramLogin(getReferralCode() || undefined)
}

const onSponsorConfirmed = (login: string | null) => {
  startTelegramLogin(login ?? undefined)
}

const cleanup = () => {
  if (stopPolling) {
    stopPolling()
    stopPolling = null
  }
}

const startTelegramLogin = async (refCode?: string) => {
  cleanup()

  isLoading.value = true
  try {
    const resume = props.tradeinResume || undefined
    const { challenge_id, ttl, bot } = await TelegramLoginPollingService.initChallenge(refCode, resume)

    const botName = bot || telegramBotFallback
    telegramBotUsername.value = botName

    if (!botName) {
      // Молчащая кнопка — худший из возможных ответов: человек нажимает и не
      // понимает, сломалось ли что-то. Показываем окно с ошибкой.
      console.warn('[GonkaGame][auth] Telegram bot username is not configured')
      modalStatus.value = 'error'
      modalErrorReason.value = 'error'
      showModal.value = true

      return
    }

    // Телефон уходит в МИНИ-АПП (`startapp`) и остаётся в Telegram: человек
    // продолжает пользоваться кабинетом там, возвращаться в браузер ему не
    // нужно, а вход в мини-аппе происходит по initData.
    //
    // Уводим по схеме `tg://` — приложение поднимается сразу, без промежуточной
    // страницы t.me; если Telegram не установлен, openTelegram сам переведёт на
    // https-адрес (см. telegramDeepLink.ts).
    //
    // Десктоп остаётся на прежнем пути: переписка с ботом в новой вкладке и
    // опрос челленджа — мини-апп на большом экране открывать некуда, а
    // подменять вкладку с опросом нельзя.
    const startParam = `${resume ? 'tgtradein_' : 'tglogin_'}${challenge_id}`
    if (props.stayOnSite) {
      if (isMobile()) openTelegram(botChatLinks(botName, startParam))
      else window.open(botChatLinks(botName, startParam).webLink, '_blank')
    } else if (isMobile()) {
      openTelegram(miniAppLinks(botName, startParam))
    } else {
      window.open(botChatLinks(botName, startParam).webLink, '_blank')
    }

    challengeTtl.value = ttl
    challengeId.value = challenge_id
    modalStatus.value = 'waiting'
    modalErrorReason.value = 'error'
    showModal.value = true

    stopPolling = TelegramLoginPollingService.startPolling(
      challenge_id,
      (token, status) => {
        // OTP-R6c: pending-ключ — не вход: не сохраняем и не перезагружаем.
        if (status.requires_approval) {
          if (status.approval_type === 'totp') {
            cleanup()
            showModal.value = false
            emit('totp-required', token)
          } else {
            modalStatus.value = 'error'
            modalErrorReason.value = 'error'
          }
          return
        }
        modalStatus.value = 'success'
        httpClient.setToken(token)
        localStorage.setItem(storageKey, token)
        // FLOW-V5 Б1 — страница переноса продолжает БЕЗ перезагрузки: та же
        // дорога «вход состоялся», что у кода из почты (профиль → страница
        // сама замечает вход и забирает перенос).
        if (props.stayOnSite && router) {
          void signInDone(router, token, undefined, 'telegram').then(() => {
            showModal.value = false
          })

          return
        }
        setTimeout(() => {
          window.location.reload()
        }, 1000)
      },
      (reason) => {
        modalStatus.value = 'error'
        modalErrorReason.value = reason
      },
    )
  } catch {
    modalStatus.value = 'error'
    modalErrorReason.value = 'error'
    showModal.value = true
  } finally {
    isLoading.value = false
  }
}

const handleCancel = () => {
  cleanup()
  showModal.value = false
}

const handleRetry = () => {
  showModal.value = false
  startTelegramLogin(getReferralCode() || undefined)
}

const handleClose = () => {
  cleanup()
  showModal.value = false
}

onUnmounted(() => {
  cleanup()
})
</script>

<style scoped lang="scss">
// Та же кнопка, что у Google в SocialAuthButtons (.social-btn--wide).
.tg-login__btn {
  width: 100%;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 0 16px;
  border-radius: var(--ds-radius-control);
  border: 1px solid var(--ds-border);
  background: var(--ds-surface);
  color: var(--ds-text-secondary);
  font-size: var(--ds-text-size-body);
  font-weight: var(--ds-weight-semibold);
  cursor: pointer;
  transition: background 0.2s;

  &:hover:not(:disabled) {
    background: var(--ds-surface-raised);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.tg-login__icon {
  width: 20px;
  height: 20px;
}
</style>
